import { CanDeactivateFn, Routes } from '@angular/router';
import { AuthGuard } from '@core/services/auth/auth-guard.service';
import { AuthorityGuard } from '@core/services/auth/authority-guard.service';
import { BreadcrumbCrumb, RESOURCE_CRUMB } from '@core/services/breadcrumb.service';
import type { WorkflowDesignerComponent } from '@modules/workflows/designer/workflow-designer/workflow-designer.component';

const WORKFLOWS_CRUMB: BreadcrumbCrumb = { label: 'Workflows', link: '/workflows' };
const RUNS_CRUMB: BreadcrumbCrumb = { label: 'Runs', link: '/runs' };
const INBOX_CRUMB: BreadcrumbCrumb = { label: 'Inbox', link: '/inbox' };
const CONNECTIONS_CRUMB: BreadcrumbCrumb = { label: 'Connections', link: '/connections' };

/** Leaving the designer through the router saves first and asks only about what could not be saved. */
const confirmLeave: CanDeactivateFn<WorkflowDesignerComponent> = designer => designer.confirmLeave();

const inboxItem = () => import('@modules/workflows/inbox/inbox-item-page.component')
  .then(m => m.InboxItemPageComponent);

/**
 * The workflow platform's pages, mounted at the root. Each names its whole trail; `fullBleed` marks a canvas page,
 * which fills the shell's content area edge to edge.
 */
export const WORKFLOW_ROUTES: Routes = [
  {
    path: 'workflows',
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'VIEW_PAGE_WORKFLOWS' },
    children: [
      {
        path: '',
        loadComponent: () => import('@modules/workflows/list/workflow-list.component').then(m => m.WorkflowListComponent),
        data: { breadcrumb: [WORKFLOWS_CRUMB] }
      },
      {
        path: ':id',
        loadComponent: () => import('@modules/workflows/designer/workflow-shell/workflow-shell.component')
          .then(m => m.WorkflowShellComponent),
        data: { breadcrumb: [WORKFLOWS_CRUMB, RESOURCE_CRUMB] },
        children: [
          {
            path: '',
            loadComponent: () => import('@modules/workflows/designer/workflow-designer/workflow-designer.component')
              .then(m => m.WorkflowDesignerComponent),
            canDeactivate: [confirmLeave],
            data: { fullBleed: true }
          },
          {
            path: 'runs',
            loadComponent: () => import('@modules/workflows/runs/workflow-runs.component').then(m => m.WorkflowRunsComponent),
            data: { breadcrumb: [{ label: 'Runs' }] }
          },
          {
            path: 'settings',
            loadComponent: () => import('@modules/workflows/settings/workflow-settings.component')
              .then(m => m.WorkflowSettingsComponent),
            data: { breadcrumb: [{ label: 'Settings' }] }
          }
        ]
      }
    ]
  },
  {
    path: 'runs',
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'VIEW_PAGE_WORKFLOWS' },
    children: [
      {
        path: '',
        loadComponent: () => import('@modules/workflows/runs/runs-page.component').then(m => m.RunsPageComponent),
        data: { breadcrumb: [RUNS_CRUMB] }
      },
      {
        path: ':id',
        loadComponent: () => import('@modules/workflows/runs/run-page/run-page.component').then(m => m.RunPageComponent),
        data: { breadcrumb: [RUNS_CRUMB, RESOURCE_CRUMB], fullBleed: true }
      }
    ]
  },
  {
    path: 'inbox',
    loadComponent: () => import('@modules/workflows/inbox/inbox-page.component').then(m => m.InboxPageComponent),
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'VIEW_PAGE_WORKFLOWS', breadcrumb: [INBOX_CRUMB] }
  },
  // The deep links Slack posts.
  {
    path: 'approvals/:id',
    loadComponent: inboxItem,
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'VIEW_PAGE_WORKFLOWS', kind: 'approval', breadcrumb: [INBOX_CRUMB, RESOURCE_CRUMB] }
  },
  {
    path: 'tasks/:id',
    loadComponent: inboxItem,
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'VIEW_PAGE_WORKFLOWS', kind: 'task', breadcrumb: [INBOX_CRUMB, RESOURCE_CRUMB] }
  },
  {
    path: 'connections',
    canActivate: [AuthGuard, AuthorityGuard],
    data: { hasAuthority: 'MANAGE_CONNECTIONS' },
    children: [
      {
        path: '',
        loadComponent: () => import('@modules/connectors/connectors.component').then(m => m.ConnectorsComponent),
        data: { breadcrumb: [CONNECTIONS_CRUMB] }
      },
      {
        path: ':id',
        loadComponent: () => import('@modules/connectors/connection-page/connection-page.component')
          .then(m => m.ConnectionPageComponent),
        data: { breadcrumb: [CONNECTIONS_CRUMB, RESOURCE_CRUMB] }
      }
    ]
  }
];
